import { Card, CodeViewer, Flex, SimpleTable, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubBlobLink, GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const CodeViewerPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>CodeViewer</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The CodeViewer component is designed to display syntax-highlighted
            code snippets in a variety of programming languages, with options to
            customize the appearance, add line numbers, enable line wrapping,
            and support copying code to the clipboard. It leverages
            SyntaxHighlighter for code formatting and integrates seamlessly with
            your application's theme, either using a light or dark theme mode.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The CodeViewer component offers multiple customization options to
            fit various use cases. You can control whether line numbers are
            shown, enable or disable line wrapping, and adjust the background
            color. It also includes an optional copy-to-clipboard feature, with
            customizable tooltips and icons.
          </Text>

          <Title level={6}>Available Variants:</Title>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Theme-based
                </Text>
                : Automatically adjusts based on light or dark mode, with
                additional control over transparent backgrounds.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Line Numbers
                </Text>
                : Choose to show or hide line numbers next to the code.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Text Wrapping
                </Text>
                : Enable or disable line wrapping for long lines of code.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Copyable
                </Text>
                : Optionally make the code copyable to the clipboard with
                customizable tooltips and icons.
              </Text>
            </li>
          </ul>

          <Title level={6}>Customization Options:</Title>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Custom Font Size
                </Text>
                : Adjust the font size of the code to fit your layout.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Custom Theme
                </Text>
                : You can provide a custom theme to override the default
                light/dark themes.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Copyable Configuration
                </Text>
                : Control how code can be copied, including custom icons and
                tooltips.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4">
            <ExpandableCard
              title="Default code viewer"
              codeViewerProps={{
                code: CodeViewerExample1,
              }}
            >
              <CodeViewer
                copyable
                language="javascript"
                code={`<Popover
  contentNode={<Input value="djuno-design" />}
  panelClassName="z-1000 min-w-600 max-w-600 whitespace-nowrap"
  panelStyle={{}}
  >
    <div className="w-40">
        <Button onClick={handleToggle}>Popover</Button>
    </div>
</Popover>`}
              />
            </ExpandableCard>

            <ExpandableCard
              title="Manual selected theme"
              codeViewerProps={{
                code: CodeViewerExample2,
              }}
            >
              <CodeViewer
                theme="dark"
                language="shell"
                copyable
                code={`npm install djuno-design --save`}
              />
            </ExpandableCard>

            <ExpandableCard
              title="Transparent background"
              codeViewerProps={{
                code: CodeViewerExample3,
              }}
            >
              <CodeViewer
                language="javascript"
                wrapLongLines
                showLineNumbers
                bgTransparent
                code={`import React from "react";
import uniquePropHOC from "./lib/unique-prop-hoc";

// this comment is here to demonstrate an extremely long line length, well beyond what you should probably allow in your own code, though sometimes you'll be highlighting code you can't refactor, which is unfortunate but should be handled gracefully

class Expire extends React.Component {
    constructor(props) {
        super(props);
        this.state = { component: props.children }
    }
    componentDidMount() {
        setTimeout(() => {
            this.setState({
                component: null
            });
        }, this.props.time || this.props.seconds * 1000);
    }
    render() {
        return this.state.component;
    }
}

export default uniquePropHOC(["time", "seconds"])(Expire);`}
              />
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* code */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">code</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">The code content to be displayed.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm" strong>
                        Required
                      </Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* language */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">language</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The programming language for syntax highlighting.
                        Supported languages can be passed as a string (e.g.,
                        "javascript").
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ICodeViewer.ts#L36"}
                        size="xs"
                        code
                      >
                        CodeViewerLanguages
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">undefined</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* showLineNumbers */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">showLineNumbers</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Whether to display line numbers alongside the code.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* wrapLongLines */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">wrapLongLines</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Enables line wrapping for long lines of code that would
                        otherwise overflow the container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* bgTransparent */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">bgTransparent</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Makes the background of the code viewer transparent,
                        otherwise it uses the default theme's background color.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* startingLineNumber */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">startingLineNumber</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The starting line number when showLineNumbers is
                        enabled.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">1</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* theme */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">theme</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Forces a specific theme regardless of the application
                        theme (light or dark).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubBlobLink("hooks/") + "useTheme.ts#L22"}
                        size="xs"
                        code
                      >
                        ThemeModes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">undefined</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* fontSize */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">fontSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Sets the font size of the code text.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        String
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">0.85rem</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* copyable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">copyable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Enables copy-to-clipboard functionality, allowing users
                        to copy the input value with an optional custom copy
                        behavior.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L67"}
                        size="xs"
                        code
                      >
                        {`CopyableProp`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const CodeViewerExample1 = `<CodeViewer
  copyable
  language="javascript"
  code={"<Popover \
  contentNode={<Input value='djuno-design' />} \
  panelClassName='z-1000 min-w-600 max-w-600 whitespace-nowrap' \
  panelStyle={{}} \
  > \
    <div className='w-40'> \
      <Button onClick={handleToggle}>Popover</Button> \
    </div> \
  </Popover>"}
/>`;

const CodeViewerExample2 = `<CodeViewer
  theme="dark"
  language="shell"
  copyable
  code="npm install djuno-design --save"
/>`;

const CodeViewerExample3 = `<CodeViewer
language="javascript"
wrapLongLines
showLineNumbers
bgTransparent
code="import React from 'react';
import uniquePropHOC from './lib/unique-prop-hoc';

// this comment is here to demonstrate an extremely long line length, well beyond what you should probably allow in your own code, though sometimes you'll be highlighting code you can't refactor, which is unfortunate but should be handled gracefully

class Expire extends React.Component {
  constructor(props) {
      super(props);
      this.state = { component: props.children }
  }
  componentDidMount() {
      setTimeout(() => {
          this.setState({
              component: null
          });
      }, this.props.time || this.props.seconds * 1000);
  }
  render() {
      return this.state.component;
  }
}

export default uniquePropHOC(['time', 'seconds'])(Expire);"
/>`;

export default CodeViewerPage;
